import * as PIXI from 'pixi.js';
import { LayoutContainer, LayoutText } from '@pixi/layout/components'; // eslint-disable-line
import { APP_WIDTH, APP_HEIGHT } from '../../utils';

import TappableGraphicsButton from '../../common/containers/TappableGraphicsButton';
import TappableTextureButton from '../../common/containers/TappableTextureButton';

const MODAL_BOX_WIDTH = 1000;
const MODAL_BOX_HEADER_HEIGHT = 160;
const MODAL_BOX_RADIUS = 40;
const MODAL_BOX_BODY_BORDER_WIDTH = 6;
const ART_WIDTH = 780;
const ART_HEIGHT = 1800;
const CONFIRM_BUTTON_WIDTH = 560;
const CONFIRM_BUTTON_HEIGHT = 140;

function costLabel(item) {
    if (item.nextClaimFree || item.price === 0) {
        return 'FREE';
    }
    return `${Number(item.price).toLocaleString()}`;
}

function claimsLabel(item) {
    if (item.maxClaims == null) {
        return item.type === 'pack'
            ? `Opened: ${item.claimCount}`
            : `Claims: ${item.claimCount}`;
    }
    return `Claims: ${item.claimCount}/${item.maxClaims}`;
}

export default class ShopItemDetailContainer extends PIXI.Container {
    static async loadAssets() {
        await PIXI.Assets.load([
            { src: '/assets/shop/close2.png', alias: 'close2' },
            { src: '/assets/shop/coin.png', alias: 'coin' },
        ]);
    }

    constructor(item, balance, width = APP_WIDTH, height = APP_HEIGHT) {
        super();

        this.item = item;
        this.balance = balance;
        this.label = 'shopItemDetail';

        this.overlay = this.buildOverlay(width, height);
        const modalBox = this.buildModalBox();
        const body = this.buildBody();

        modalBox.addChild(this.buildHeader(item));
        body.addChild(this.buildImage(item));
        body.addChild(this.buildContent(item, balance));
        modalBox.addChild(body);
        this.overlay.addChild(modalBox);
        this.addChild(this.overlay);
        this.buildModalBackground(modalBox);
    }

    buildOverlay(width, height) {
        const overlay = new LayoutContainer();
        overlay.label = 'shopItemDetailOverlay';
        overlay.layout = {
            width,
            height,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
        };
        overlay.eventMode = 'static';
        overlay.on('pointerdown', (event) => {
            event.stopPropagation();
            this.onBackgroundClicked?.();
        });
        return overlay;
    }

    buildModalBox() {
        const modalBox = new LayoutContainer({
            layout: {
                width: MODAL_BOX_WIDTH,
                flexDirection: 'column',
            },
        });
        modalBox.eventMode = 'static';
        modalBox.on('pointerdown', (event) => {
            event.stopPropagation();
        });
        return modalBox;
    }

    buildHeader(item) {
        const header = new LayoutContainer({
            layout: {
                width: MODAL_BOX_WIDTH,
                height: MODAL_BOX_HEADER_HEIGHT,
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 10,
            },
        });
        header.addChild(this.buildTitle(item));
        if (item.type === 'pack') {
            header.addChild(this.buildPackId(item));
        } else if (item.type === 'starter_deck') {
            header.addChild(this.buildStarterDeckId(item));
        }
        header.addChild(this.buildCloseButton());
        return header;
    }

    buildCloseButton() {
        const CLOSE_BUTTON_WIDTH = 109;
        const CLOSE_BUTTON_HEIGHT = 113;

        const closeButton = new TappableTextureButton({
            texture: PIXI.Assets.get('close2'),
            width: CLOSE_BUTTON_WIDTH,
            height: CLOSE_BUTTON_HEIGHT,
        });
        closeButton.label = 'shopItemDetailClose';
        closeButton.layout = {
            position: 'absolute',
            right: 24,
        };
        closeButton.onButtonTap = () => {
            this.onBackgroundClicked?.();
        };
        return closeButton;
    }

    buildTitle(item) {
        const titleGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });

        let title = '';

        if (item.type === 'pack') {
            title = item.packName;
        } else if (item.type === 'starter_deck') {
            title = item.deckName;
        }

        const titleText = new LayoutText({
            text: title,
            style: {
                fontFamily: 'Caladea',
                fontSize: 64,
                fill: titleGradient,
                fontWeight: 'bold',
                stroke: { color: '#422400', width: 5 },
            },
            layout: {
                height: 100,
                objectPosition: 'center',
                objectFit: 'none',
            },
        });

        if (titleText.slot.width > 550) {
            titleText.layout = { width: 550, objectFit: 'scale-down' };
        } else {
            titleText.layout = { width: titleText.slot.width }; 
        }
        
        return titleText;
    }

    buildPackId(item) {
        const packIdText = new LayoutText({
            text: item.packId,
            style: {
                fontFamily: 'Fugaz One',
                fontSize: 32,
                fill: '#ffffff',
            },
            layout: {
                height: 60,
                objectPosition: 'center',
                objectFit: 'none',
                backgroundColor: '#131a2a',
                borderRadius: 30,
            },
        });

        packIdText.layout = { width: packIdText.slot.width + 40 };

        return packIdText;
    }

    buildStarterDeckId(item) {
        const starterDeckIdText = new LayoutText({
            text: item.prebuiltDeckId,
            style: {
                fontFamily: 'Fugaz One',
                fontSize: 32,
                fill: '#ffffff',
            },
            layout: {
                height: 60,
                objectPosition: 'center',
                objectFit: 'none',
                backgroundColor: '#131a2a',
                borderRadius: 30,
            },
        });

        starterDeckIdText.layout = { width: starterDeckIdText.slot.width + 40 };

        return starterDeckIdText;
    }

    buildBody() {
        return new LayoutContainer({
            layout: {
                width: MODAL_BOX_WIDTH,
                flexDirection: 'column',
                alignItems: 'center',
                padding: 40,
                paddingTop: 0,
                paddingBottom: 0,
            },
        });
    }

    buildImage(item) {
        const imageContainer = new LayoutContainer({
            layout: {
                width: '100%',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#131b28',
                borderRadius: 24,
                padding: 60,
            },
        });
        imageContainer.addChild(this.buildArt(item));
        return imageContainer;
    }

    buildContent(item, balance) {
        const content = new LayoutContainer();
        content.layout = {
            width: MODAL_BOX_WIDTH,
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 36,
            paddingBottom: 48,
            gap: 20,
        };
        // content.addChild(this.buildName(item));
        // content.addChild(this.buildClaims(item));

        const status = this.buildStatus(item);
        if (status) {
            content.addChild(status);
        }

        content.addChild(this.buildConfirmButton(item));
        content.addChild(this.buildYourBalance(balance));
        return content;
    }

    buildName(item) {
        const nameText = new LayoutText({
            text: item.name,
            style: {
                fontFamily: 'Open Sans',
                fontSize: 40,
                fontWeight: '700',
                fill: '#1a1a1a',
                wordWrap: true,
                wordWrapWidth: MODAL_BOX_WIDTH - 80,
                align: 'center',
            },
        });
        nameText.layout = {
            width: MODAL_BOX_WIDTH - 80,
            height: 110,
            objectPosition: 'center',
        };
        return nameText;
    }

    buildClaims(item) {
        const metaText = new LayoutText({
            text: claimsLabel(item),
            style: {
                fontFamily: 'Open Sans',
                fontSize: 32,
                fontWeight: '600',
                fill: '#5c6b82',
                align: 'center',
            },
        });
        metaText.layout = {
            width: MODAL_BOX_WIDTH - 80,
            height: 44,
            objectPosition: 'center',
        };
        return metaText;
    }

    buildStatus(item) {
        if (item.canClaim) {
            return null;
        }

        const statusText = new LayoutText({
            text: item.maxClaims != null && item.claimCount >= item.maxClaims
                ? 'No claims left'
                : 'Not enough coins',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 35,
                fontWeight: '700',
                fill: '#b44b4b',
                align: 'center',
            },
        });
        statusText.layout = {
            width: MODAL_BOX_WIDTH - 80,
            height: 40,
            objectPosition: 'center',
            marginBottom: 20,
        };
        return statusText;
    }

    buildConfirmButton(item) {
        const confirmButtonGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#6eaa46' },
                { offset: 1, color: '#2d5b27' },
            ],
        });

        const notEnoughBalanceGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#787878' },
                { offset: 1, color: '#414141' },
            ],
        });

        const canConfirm = item.canClaim;
        const confirmButton = new TappableGraphicsButton({
            text: costLabel(item),
            icon: PIXI.Assets.get('coin'),
            iconWidth: 80,
            iconHeight: 80,
            iconGap: 5,
            iconPosition: 'left',
            iconMode: 'inline',
            width: CONFIRM_BUTTON_WIDTH,
            height: CONFIRM_BUTTON_HEIGHT,
            cornerRadius: 24,
            fill: canConfirm ? confirmButtonGradient : notEnoughBalanceGradient,
            shadowColor: canConfirm ? '#132110' : '#181818',
            shadowOffsetY: 13,
            backgroundColor: canConfirm ? '#3b72f5' : '#8d93a3',
            borderColor: canConfirm ? '#96da5d' : '#9b9b9b',
            outerBorderColor: canConfirm ? '#4f7834' : '#565656',
            borderWidth: 5,
            fontColor: '#ffffff',
            fontSize: 60,
            fontWeight: '700',
            fontSpacing: 3,
            fontFamily: 'Fugaz One',
            fontStrokeColor: canConfirm ? '#2b4a22' : '#414141',
            fontStrokeWidth: 7,
            contentOpacity: canConfirm ? 1 : 0.5,
        });
        if (canConfirm) {
            confirmButton.onButtonTap = () => {
                this.onConfirm?.();
            };
        }
        return confirmButton;
    }

    buildArt(item) {
        if (item.image && PIXI.Assets.get(item.image)) {
            const itemSprite = new PIXI.Sprite(PIXI.Assets.get(item.image));
            itemSprite.layout = {
                height: 880,
                objectFit: 'cover',
            };
            return itemSprite;
        }

        if (item.leader && PIXI.Assets.get(`/proxy/image/${item.leader}.png`)) {
            const leaderSprite = new PIXI.Sprite(PIXI.Assets.get(`/proxy/image/${item.leader}.png`));
            leaderSprite.layout = {
                width: '100%',
                objectFit: 'contain',
            };
            return leaderSprite;
        }

        const placeholder = new LayoutContainer();
        placeholder.layout = {
            width: ART_WIDTH,
            height: ART_HEIGHT,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#3b72f5',
            borderRadius: 16,
        };
        const packLabel = new LayoutText({
            text: 'PACK',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 48,
                fontWeight: '700',
                fill: '#ffffff',
            },
        });
        placeholder.addChild(packLabel);
        return placeholder;
    }

    buildModalBackground(modalBox) {
        this.modalBackground = new PIXI.Graphics();
        modalBox.addChildAt(this.modalBackground, 0);
        modalBox.on('layout', () => {
            const gradient = new PIXI.FillGradient({
                type: 'linear',
                start: { x: 0, y: 0 },
                end: { x: 0, y: 1 },
                colorStops: [
                    { offset: 0, color: '#41557c' },
                    { offset: 1, color: '#1f2b3f' },
                ],
            });

            this.modalBackground.clear();
            this.modalBackground.roundRect(0, 0, modalBox.layout.computedLayout.width, modalBox.layout.computedLayout.height, MODAL_BOX_RADIUS);
            this.modalBackground.fill(gradient);
            this.modalBackground.stroke({ color: '#566685', width: MODAL_BOX_BODY_BORDER_WIDTH });
        });
    }

    buildYourBalance(balance) {
        const FONT_SIZE = 35;

        const yourBalanceRow = new LayoutContainer({
            layout: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                marginTop: 40,
            },
        });

        const yourBalanceText = new LayoutText({
            text: `Your Balance:`,
            style: {
                fontFamily: 'Open Sans',
                fontSize: FONT_SIZE,
                fill: '#8592a5',
                fontWeight: '600',
            },
            layout: {
                height: FONT_SIZE + 10,
                objectPosition: 'center',
                objectFit: 'none',
            },
        });
        yourBalanceText.layout = {
            width: yourBalanceText.slot.width,
        };
        yourBalanceRow.addChild(yourBalanceText);

        const BALANCE_PILL_HEIGHT = 66;
        const COIN_SIZE = 60;
        const BALANCE_PADDING = 35;

        const balancePill = new LayoutContainer({
            layout: {
                height: BALANCE_PILL_HEIGHT,
                flexDirection: 'row',
                alignItems: 'center',
                paddingLeft: BALANCE_PADDING,
                paddingRight: BALANCE_PADDING,
                backgroundColor: '#171e2e',
                borderRadius: BALANCE_PILL_HEIGHT / 2,
                borderColor: '#0f1826',
                borderWidth: 4,
            },
        });

        const coinIcon = new PIXI.Sprite(PIXI.Assets.get('coin'));
        coinIcon.width = COIN_SIZE;
        coinIcon.height = COIN_SIZE;
        coinIcon.layout = {
            width: COIN_SIZE,
            height: COIN_SIZE,
        };
        balancePill.addChild(coinIcon);

        const balanceText = new LayoutText({
            text: `${Number(balance).toLocaleString()}`,
            style: {
                fontFamily: 'Open Sans',
                fontSize: FONT_SIZE,
                fill: '#eff2f2',
                fontWeight: '600',
            },
            layout: {
                height: FONT_SIZE + 10,
                objectPosition: 'center',
                objectFit: 'none',
            },
        });
        balanceText.layout = {
            width: balanceText.slot.width,
        };
        balancePill.addChild(balanceText);
        yourBalanceRow.addChild(balancePill);
        return yourBalanceRow;
    }

    resize(width, height) {
        this.overlay?.layout?.setStyle({ width, height });
    }
}
